import { announceGlobalLiveRegion } from '@theme/live-region';

// <s-search-suggest> — sitewide search bar dropdown backed by Shopify's native predictive search endpoint.
// Combobox pattern: focus stays on the input, arrows move aria-activedescendant, Enter opens the active option
// or submits the native GET form when none is active. Stale requests are aborted.
const MIN_CHARS = 2;
const DEBOUNCE_MS = 200;
const LIMIT = 6;

class SearchSuggest extends HTMLElement {
  #ac;
  #request;
  #timer;
  #input;
  #panel;
  #listbox;
  #status;
  #optionTemplate;
  #activeIndex = -1;
  #formatter;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#input = this.querySelector('[data-suggest-input]');
    this.#panel = this.querySelector('[data-suggest-panel]');
    this.#listbox = this.querySelector('[data-suggest-listbox]');
    this.#status = this.querySelector('[data-suggest-status]');
    this.#optionTemplate = this.querySelector('template[data-suggest-option]');
    if (!this.#input || !this.#panel || !this.#listbox || !this.#status || !this.#optionTemplate) return;

    try {
      this.#formatter = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'AUD' });
    } catch {
      this.#formatter = null;
    }

    this.#input.addEventListener('input', () => this.#schedule(), opts);
    this.#input.addEventListener('keydown', (event) => this.#onKeydown(event), opts);
    this.#input.addEventListener('focus', () => {
      if (this.#listbox.childElementCount > 0 || !this.#status.hidden) this.#open();
    }, opts);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.#close();
    }, opts);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.#close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  get #options() {
    return [...this.#listbox.querySelectorAll('[role="option"]')];
  }

  get #terms() {
    return this.#input.value.trim();
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#request?.abort();
    if (this.#terms.length < MIN_CHARS) {
      this.#reset();
      return;
    }
    this.#showStatus(this.dataset.loadingText, true);
    this.#timer = setTimeout(() => this.#fetch(this.#terms), DEBOUNCE_MS);
  }

  async #fetch(terms) {
    this.#request?.abort();
    this.#request = new AbortController();
    const url = new URL(this.dataset.endpoint, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(LIMIT));
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: this.#request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await response.json();
      if (terms !== this.#terms) return;
      const products = data?.resources?.results?.products ?? [];
      this.#render(products.slice(0, LIMIT), terms);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#clearOptions();
      this.#showStatus(this.dataset.errorText, false);
      this.#appendViewAll(terms);
    }
  }

  #render(products, terms) {
    this.#clearOptions();
    if (products.length === 0) {
      this.#showStatus(this.dataset.emptyText.replace('[terms]', terms), false);
      this.#appendViewAll(terms);
      announceGlobalLiveRegion(this.dataset.emptyText.replace('[terms]', terms), { frames: 2 });
      return;
    }
    this.#status.hidden = true;

    products.forEach((product, index) => {
      const fragment = this.#optionTemplate.content.cloneNode(true);
      const option = fragment.querySelector('[role="option"]');
      const link = fragment.querySelector('a');
      const image = fragment.querySelector('img');
      const title = fragment.querySelector('[data-slot="title"]');
      const price = fragment.querySelector('[data-slot="price"]');
      const productUrl = product.url || '';

      option.id = `${this.id}-option-${index}`;
      option.dataset.url = productUrl;
      link.href = productUrl;
      title.textContent = product.title || '';
      price.textContent = this.#price(product);

      const src = product.featured_image?.url || product.image;
      if (src) {
        const sized = new URL(src, window.location.origin);
        sized.searchParams.set('width', '120');
        image.src = sized.toString();
        image.alt = '';
      } else {
        image.remove();
      }
      this.#listbox.append(fragment);
    });

    this.#appendViewAll(terms);
    this.#open();
    announceGlobalLiveRegion(this.dataset.countText.replace('[count]', String(products.length)), { frames: 2 });
  }

  #appendViewAll(terms) {
    const fragment = this.querySelector('template[data-suggest-view-all]').content.cloneNode(true);
    const option = fragment.querySelector('[role="option"]');
    const link = fragment.querySelector('a');
    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('type', 'product');
    option.id = `${this.id}-option-all`;
    option.dataset.url = url.pathname + url.search;
    link.href = option.dataset.url;
    this.#listbox.append(fragment);
    this.#open();
  }

  #price(product) {
    const raw = product.price ?? product.price_min;
    const value = typeof raw === 'string' ? Number.parseFloat(raw) : Number(raw);
    if (!Number.isFinite(value)) return '';
    const formatted = this.#formatter ? this.#formatter.format(value) : String(value);
    const min = Number.parseFloat(product.price_min);
    const max = Number.parseFloat(product.price_max);
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
      return this.dataset.fromText.replace('[price]', this.#formatter ? this.#formatter.format(min) : String(min));
    }
    return formatted;
  }

  #showStatus(text, loading) {
    this.#status.textContent = text;
    this.#status.hidden = false;
    this.#status.toggleAttribute('data-loading', loading);
    this.#open();
  }

  #clearOptions() {
    this.#listbox.replaceChildren();
    this.#setActive(-1);
  }

  #reset() {
    this.#request?.abort();
    this.#clearOptions();
    this.#status.hidden = true;
    this.#close();
  }

  #open() {
    this.#panel.hidden = false;
    this.#input.setAttribute('aria-expanded', String(this.#options.length > 0));
  }

  #close() {
    this.#panel.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  #setActive(index) {
    const options = this.#options;
    for (const option of options) option.setAttribute('aria-selected', 'false');
    if (index < 0 || options.length === 0) {
      this.#activeIndex = -1;
      this.#input.removeAttribute('aria-activedescendant');
      return;
    }
    const next = (index + options.length) % options.length;
    this.#activeIndex = next;
    options[next].setAttribute('aria-selected', 'true');
    this.#input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({ block: 'nearest' });
  }

  #onKeydown(event) {
    const options = this.#options;
    switch (event.key) {
      case 'ArrowDown':
        if (options.length === 0) return;
        event.preventDefault();
        if (this.#panel.hidden) this.#open();
        this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        if (options.length === 0) return;
        event.preventDefault();
        if (this.#panel.hidden) this.#open();
        this.#setActive(this.#activeIndex < 0 ? options.length - 1 : this.#activeIndex - 1);
        break;
      case 'Enter':
        if (!this.#panel.hidden && this.#activeIndex >= 0) {
          event.preventDefault();
          const url = options[this.#activeIndex]?.dataset.url;
          if (url) window.location.assign(url);
        }
        // Otherwise the native GET form submits.
        break;
      case 'Escape':
        if (!this.#panel.hidden) {
          event.preventDefault();
          this.#close();
        } else if (this.#input.value) {
          event.preventDefault();
          this.#input.value = '';
          this.#reset();
        }
        break;
      case 'Tab':
        this.#close();
        break;
    }
  }
}

if (!customElements.get('s-search-suggest')) customElements.define('s-search-suggest', SearchSuggest);
